import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserProfile, updateUserProfile, deleteUser, suspendUser } from '../api/Users';
import { getAllProfessions, Profession } from '../api/Professions';
import { User } from '../types';

const AdminUserEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    
    Promise.all([
      getUserProfile(id),
      getAllProfessions()
    ])
      .then(([userData, professionsData]) => {
        console.log('User data loaded:', userData);
        console.log('User professionID:', userData.professionID);
        console.log('Professions loaded:', professionsData);
        setUser(userData);
        setProfessions(professionsData);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Failed to load user.');
        console.error('AdminUserEdit failed to load user:', err);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!user) return;
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Only send the fields that should be updated
      const updates = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        company: user.company,
        address: user.address,
        city: user.city,
        state: user.state,
        zip: user.zip,
        professionID: user.professionID,
        role: user.role,
        isEmailVerified: user.isEmailVerified
      };
      
      console.log('Sending updates:', updates);
      console.log('User ID:', user.id);
      
      const updatedUser = await updateUserProfile(user.id, updates);
      
      console.log('Received updated user:', updatedUser);
      
      setUser(updatedUser);
      setSuccess('User updated successfully.');
      
      // Reload to ensure we have fresh data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update user:', err);
      console.error('Error response:', err?.response);
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to update user.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    
    // Validate email confirmation
    if (deleteConfirmEmail !== user.email) {
      setError('Email confirmation does not match. Deletion cancelled.');
      return;
    }
    
    setDeleting(true);
    setError('');
    
    try {
      await deleteUser(user.id);
      setShowDeleteModal(false);
      setSuccess('User deleted successfully');
      // Navigate to admin panel (user list page) after a brief delay
      setTimeout(() => {
        navigate('/admin/panel');
      }, 1000);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to delete user.');
      setDeleting(false);
    }
  };

  const handleSuspend = async () => {
    if (!user) return;
    setSuspending(true);
    setError('');
    try {
      const target = !user.suspended;
      await suspendUser(user.id, target);
      setUser({ ...user, suspended: target });
      setSuccess(target ? 'User suspended.' : 'User unsuspended.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update suspension status.');
    } finally {
      setSuspending(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setDeleteConfirmEmail('');
    setError('');
    setShowDeleteModal(true);
  };

  if (loading && !user) return <div className="container mt-4">Loading...</div>;
  if (error && !user) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
  if (!user) return <div>User not found.</div>;

  return (
    <div className="container mt-4" style={{ maxWidth: 960 }}>

      {/* Page header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-0">Edit User</h2>
          <small className="text-muted">{user.email}</small>
        </div>
        <span
          className="badge"
          style={{ background: user.suspended ? '#6B7280' : '#10B981', color: '#fff', fontSize: '0.8rem', padding: '6px 12px' }}
        >
          {user.suspended ? 'Suspended' : 'Active'}
        </span>
      </div>

      {error   && <div className="alert alert-danger  py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      {/* Account meta strip */}
      <div className="card mb-4">
        <div className="card-header cec-section-header">
          <span className="cec-section-title">Account Information</span>
        </div>
        <div className="card-body py-3">
          <div className="row g-3">
            <div className="col-6 col-md-3">
              <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Registered</div>
              <div style={{ fontWeight: 600 }}>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div className="col-6 col-md-3">
              <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Licenses</div>
              <div style={{ fontWeight: 600 }}>{user.licenses?.length || 0}</div>
            </div>
            <div className="col-6 col-md-3">
              <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</div>
              <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                {user.role?.toUpperCase()}
              </span>
            </div>
            <div className="col-6 col-md-3">
              <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Status</div>
              <span className={`badge ${user.isEmailVerified ? 'bg-success' : 'bg-warning text-dark'}`}>
                {user.isEmailVerified ? 'Verified' : 'Not Verified'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={e => { e.preventDefault(); handleSave(); }}>

        {/* ── Identity ────────────────────────────────────────── */}
        <div className="card mb-3">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Identity</span>
          </div>
          <div className="card-body py-3">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label mb-1">First Name</label>
                <input className="form-control" name="firstName" value={user.firstName || ''} onChange={handleChange} />
              </div>
              <div className="col-md-4">
                <label className="form-label mb-1">Last Name</label>
                <input className="form-control" name="lastName" value={user.lastName || ''} onChange={handleChange} />
              </div>
              <div className="col-md-4">
                <label className="form-label mb-1">Email</label>
                <input className="form-control" name="email" value={user.email} onChange={handleChange} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Account Settings ────────────────────────────────── */}
        <div className="card mb-3">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Account Settings</span>
          </div>
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label mb-1">Profession</label>
                <select className="form-select" name="professionID" value={user.professionID || ''} onChange={handleChange}>
                  <option value="">Select Profession</option>
                  {professions.filter(p => p.isActive !== false).map(p => (
                    <option key={p.professionID} value={p.professionID}>{p.name} ({p.professionID})</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">Role</label>
                <select className="form-select" name="role" value={user.role || 'user'} onChange={handleChange}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="col-md-3 pb-1">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isEmailVerified"
                    checked={user.isEmailVerified || false}
                    onChange={e => setUser({ ...user, isEmailVerified: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="isEmailVerified">Email Verified</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Contact & Address ───────────────────────────────── */}
        <div className="card mb-4">
          <div className="card-header cec-section-header">
            <span className="cec-section-title">Contact &amp; Address</span>
          </div>
          <div className="card-body py-3">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label mb-1">Phone</label>
                <input className="form-control" name="phone" value={user.phone || ''} onChange={handleChange} />
              </div>
              <div className="col-md-8">
                <label className="form-label mb-1">Company</label>
                <input className="form-control" name="company" value={user.company || ''} onChange={handleChange} />
              </div>
              <div className="col-12">
                <label className="form-label mb-1">Address</label>
                <input className="form-control" name="address" value={user.address || ''} onChange={handleChange} />
              </div>
              <div className="col-md-5">
                <label className="form-label mb-1">City</label>
                <input className="form-control" name="city" value={user.city || ''} onChange={handleChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">State</label>
                <input className="form-control" name="state" value={user.state || ''} onChange={handleChange} maxLength={2} placeholder="CA" />
              </div>
              <div className="col-md-4">
                <label className="form-label mb-1">Zip</label>
                <input className="form-control" name="zip" value={user.zip || ''} onChange={handleChange} maxLength={10} placeholder="12345" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex gap-2">
            <button
              className={`btn ${user.suspended ? 'btn-success' : 'btn-warning'}`}
              type="button"
              onClick={handleSuspend}
              disabled={suspending}
            >
              {suspending ? 'Updating...' : user.suspended ? 'Unsuspend User' : 'Suspend User'}
            </button>
            <button className="btn btn-danger" type="button" onClick={handleOpenDeleteModal}>
              Delete User
            </button>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-secondary" type="button" onClick={() => navigate(-1)}>Back</button>
            <button className="btn btn-primary" type="submit">Save Changes</button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm User Deletion</h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3">
                  <strong>This will deactivate the account. Data is retained and can be restored.</strong>
                </div>

                <p className="mb-3">
                  You are about to <strong>soft-delete</strong> user:
                </p>
                
                <div className="card mb-3">
                  <div className="card-body">
                    <p className="mb-1"><strong>Email:</strong> {user.email}</p>
                    <p className="mb-1"><strong>Name:</strong> {user.firstName} {user.lastName}</p>
                    <p className="mb-0"><strong>Role:</strong> {user.role}</p>
                  </div>
                </div>

                <p className="mb-2">The account will be hidden from all admin views and the user will no longer be able to log in. No data is permanently removed.</p>

                <div className="border-top pt-3">
                  <label htmlFor="confirmEmail" className="form-label">
                    <strong>To confirm, type the user's email address:</strong>
                  </label>
                  <input
                    type="text"
                    id="confirmEmail"
                    className="form-control"
                    placeholder={user.email}
                    value={deleteConfirmEmail}
                    onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                    disabled={deleting}
                    autoComplete="off"
                  />
                  <small className="text-muted">
                    Type: <code>{user.email}</code>
                  </small>
                </div>

                {error && (
                  <div className="alert alert-danger mt-3 mb-0">
                    {error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDelete}
                  disabled={deleting || deleteConfirmEmail !== user.email}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    'Confirm Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserEdit;
